import { Resource } from "@domain/base/Resource";
import { IProductFlowConnectorRepository } from "@domain/connection/IMarketplaceRepository";
import { Connection } from "@domain/connection/models/Connection";
import { ConnectionSettings, ProductFlowSettings } from "@domain/connection/models/ConnectionSettings";
import { ConnectionMapper } from "../mappers/MarketplaceMapper";
import { ProductFlowConnectorDataSource } from "../datasources/ProductFlowConnectorDataSource";
import { InventorySyncEventItem } from '@domain/connection/models/InventorySyncEvent';
import { FullSyncJob } from '@domain/connection/models/ImportJob';

export class ProductFlowConnectorRepository implements IProductFlowConnectorRepository {
    constructor(private dataSource: ProductFlowConnectorDataSource) { }

    async addProductFlowConnection(xCompanyId: string, acceptLanguage: string, personalAccessTokenKey: string): Promise<Resource<Connection>> {
        try {
            const data = await this.dataSource.addConnection(xCompanyId, acceptLanguage, personalAccessTokenKey);
            return {
                status: 'success',
                data: ConnectionMapper.toConnectionModel(data)
            };
        } catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to add ProductFlow connection'
                }
            };
        }
    }

    async syncProducts(connectionId: string): Promise<Resource<{ jobId: string }>> {
        try {
            const data = await this.dataSource.syncProducts(connectionId);
            return { status: 'success', data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync ProductFlow products' } };
        }
    }

    async syncInventory(connectionId: string): Promise<Resource<{ jobId: string }>> {
        try {
            const data = await this.dataSource.syncInventory(connectionId);
            return { status: 'success', data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync ProductFlow inventory' } };
        }
    }

    async syncMarketplaceOffers(connectionId: string): Promise<Resource<{ jobId: string }>> {
        try {
            const data = await this.dataSource.syncMarketplaceOffers(connectionId);
            return { status: 'success', data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync ProductFlow marketplace offers' } };
        }
    }
    
    async getMarketplaces(connectionId: string): Promise<Resource<{ machine_name: string, connection_id: string, slug: string, name: string }[]>> {
        try {
            const data = await this.dataSource.getMarketplaces(connectionId);
            return { status: 'success', data: data };
        }
        catch (error) {
            return {
                status: 'error',
                error: {
                    type: 'UnknownError',
                    message: 'Failed to get ProductFlow marketplaces'
                }
            };
        }
    }

    async fetchWarehouseInventory(connectionId: string): Promise<Resource<InventorySyncEventItem[]>> {
        try {
            const data = await this.dataSource.fetchWarehouseInventory(connectionId);
            return { status: 'success', data: data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to fetch warehouse inventory' } };
        }
    }

    async fetchMarketplaceInventory(connectionId: string, marketplaceSlug: string): Promise<Resource<InventorySyncEventItem[]>> {
        try {
            const data = await this.dataSource.fetchMarketplaceInventory(connectionId, marketplaceSlug);
            return { status: 'success', data: data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to fetch marketplace inventory' } };
        }
    }

    async syncAllOrders(connectionId: string): Promise<Resource<{ jobId: string }>> {
        try {
            const data = await this.dataSource.syncAllOrders(connectionId);
            return { status: 'success', data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync ProductFlow orders' } };
        }
    }

    async syncNewOrders(connectionId: string): Promise<Resource<{ jobId: string }>> {

        try {
            const data = await this.dataSource.syncNewOrders(connectionId);
            return { status: 'success', data };
        }
        catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to sync ProductFlow new orders' } };
        }
    }

    async getSettings(connectionId: string): Promise<Resource<ProductFlowSettings>> {
        try {
            const data = await this.dataSource.getSettings(connectionId);
            return { status: 'success', data: data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to get connection settings' } };
        }
    }

    async updateSettings(connectionId: string, settings: Partial<ProductFlowSettings>): Promise<Resource<ProductFlowSettings>> {
        try {
            const data = await this.dataSource.updateSettings(connectionId, settings);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to update connection settings' } };
        }
    }

    async syncFull(connectionId: string): Promise<Resource<FullSyncJob[]>> {
        try {
            const data = await this.dataSource.syncFull(connectionId);
            return { status: 'success', data };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to start full sync' } };
        }
    }

    async reconnect(connectionId: string, xCompanyId: string, acceptLanguage: string, personalAccessTokenKey: string): Promise<Resource<Connection>> {
        try {
            const data = await this.dataSource.reconnect(connectionId, xCompanyId, acceptLanguage, personalAccessTokenKey);
            return { status: 'success', data: ConnectionMapper.toConnectionModel(data) };
        } catch (error) {
            return { status: 'error', error: { type: 'UnknownError', message: 'Failed to reconnect ProductFlow connection' } };
        }
    }
}

